import { Icon } from "@mdi/react";
import React, { FC, useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  mdiArrowCollapseAll,
  mdiChevronDown,
  mdiChevronRight,
  mdiContentCopy,
  mdiDelete,
  mdiDotsVertical,
  mdiPlus
} from "@mdi/js";
import { Button, Input, Modal, Popover, Tooltip, Typography } from "antd";
import { arrayOrEmpty, uid } from "utils";

import { IconButton } from "components/forecasting/Shared";

import useAddAutomationGroup from "../hooks/useAddAutomationGroup";
import useDeleteAutomationGroup from "../hooks/useDeleteAutomationGroup";
import useUpdateAutomationCollection from "../hooks/useUpdateAutomationCollection";
import useUpdateAutomationGroup from "../hooks/useUpdateAutomationGroup";
import {
  AutomationCollection,
  AutomationCommand,
  AutomationGroup
} from "../types/automation";
import { AutomationPayloads } from "../types/payloads";
import { commandsTemplate } from "../types/templates";
import { AddCommandModal } from "./AddCommandModal";
import { getRenderComponent, renderComponentMap } from "./renderComponentsMap";
import {
  CollapseAllButton,
  CommandItem,
  CommandsContainer,
  CommandsListSection,
  DragHint,
  GroupHeader,
  GroupTitle,
  GroupWrapper,
  GroupsContainer,
  HeaderBar,
  PropertiesContent,
  PropertiesHeader,
  PropertiesInner,
  PropertiesScrollable
} from "./styles/AutomationCommandsList.styles";

interface AutomationCommandsProps {
  selectedAutomation: AutomationCollection;
  selectedCommand: AutomationCommand;
  onGroupsChange: (commands: AutomationGroup[]) => void;
  onSelectedCommand: (command: AutomationCommand) => void;
}

const AutomationCommands: FC<AutomationCommandsProps> = ({
  selectedAutomation,
  selectedCommand,
  onSelectedCommand,
  onGroupsChange
}) => {
  const containerRef = useRef<HTMLHtmlElement>();
  const [draggedGroup, setDraggedGroup] = useState<string | null>(null);
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);
  const [dragPosition, setDragPosition] = useState<"before" | "after" | null>(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);

  const { mutateAsync: updateAutomation } = useUpdateAutomationCollection();
  const [renameTargetGroup, setRenameTargetGroup] = useState<AutomationGroup | null>(
    null
  );
  const [openAddCommandModal, setOpenAddCommandModal] = useState<boolean>(false);
  const [renameValue, setRenameValue] = useState("");

  // Collapsed state per group
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  // Popover open state per group
  const [popoverOpen, setPopoverOpen] = useState<string | null>(null);

  const { mutateAsync: addAutomationGroup } = useAddAutomationGroup();
  const { mutateAsync: deleteAutomationGroup } = useDeleteAutomationGroup();
  const { mutateAsync: updateAutomationGroup } = useUpdateAutomationGroup();

  const handleSettingsChange = useCallback(
    async (payload: AutomationPayloads) => {
      selectedCommand.payload = payload;
      const groups = arrayOrEmpty(selectedAutomation?.groups);

      const groupIndex = groups.findIndex(
        (cmd) => cmd.commands.findIndex((c) => c.id == selectedCommand.id) >= 0
      );
      if (groupIndex < 0) {
        return;
      }
      const newSelected = { ...selectedCommand };
      const foundGroup = groups[groupIndex];
      const cmdIndex = foundGroup.commands.findIndex(
        (cmd) => cmd.id == selectedCommand.id
      );
      if (cmdIndex < 0) {
        return;
      }
      const updatedCommands = [...foundGroup.commands];
      updatedCommands.splice(cmdIndex, 1, newSelected);
      foundGroup.commands = updatedCommands;
      try {
        await updateAutomationGroup({
          id: selectedAutomation._id,
          groupId: foundGroup.id,
          group: foundGroup
        });
        groups.splice(groupIndex, 1, foundGroup);
        onGroupsChange(groups);
        onSelectedCommand(newSelected);
      } catch (err) {
        toast.error("Error updating");
      }
    },
    [selectedCommand, selectedAutomation]
  );
  const getSettingsComponent = useCallback(
    (command: AutomationCommand, onChange: (payload: AutomationPayloads) => void) => {
      if (!command) {
        return null;
      }
      const Component = renderComponentMap[command.topic];
      return Component?.Settings ? (
        <Component.Settings value={command.payload} onChange={onChange} />
      ) : null;
    },
    [selectedCommand]
  );
  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };
  const duplicateGroup = async (group: AutomationGroup) => {
    // Helper to generate new ids (replace with your own id generator if needed)
    const newId = () => Math.random().toString(36).slice(2, 10);

    const clonedGroup: AutomationGroup = {
      ...group,
      id: newId(),
      commands: group.commands.map((cmd) => ({
        ...cmd,
        id: newId()
      }))
    };
    try {
      await addAutomationGroup({
        id: selectedAutomation?._id,
        group: clonedGroup
      });
      const groups = arrayOrEmpty(selectedAutomation?.groups);
      // Insert after the original group
      const idx = groups.findIndex((g) => g.id === group.id);
      const newGroups = [
        ...groups.slice(0, idx + 1),
        clonedGroup,
        ...groups.slice(idx + 1)
      ];
      onGroupsChange(newGroups);
    } catch (err) {
      toast.error("Error duplicating group");
    } finally {
      setPopoverOpen(null);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      const groups = arrayOrEmpty(selectedAutomation?.groups);
      const newGroups = groups.filter((g) => g.id !== groupId);
      await deleteAutomationGroup({
        groupId,
        id: selectedAutomation._id
      });
      onGroupsChange(newGroups);
      setPopoverOpen(null);
    } catch (err) {
      toast.error("Error deleting automation.");
    }
  };

  const handleGroupDragStart = (e: React.DragEvent, groupId: string) => {
    e.stopPropagation();
    setDraggedGroup(groupId);
    e.dataTransfer.setData("text/plain", groupId);
    e.dataTransfer.effectAllowed = "move";
  };
  // Handle drag start
  const handleGroupDragOver = (e: React.DragEvent, groupId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedGroup === groupId) return;

    setDragOverGroup(groupId);

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const y = e.clientY - rect.top;

    if (y < rect.height / 2) {
      setDragPosition("before");
    } else {
      setDragPosition("after");
    }
  };

  // Handle group drag leave
  const handleGroupDragLeave = () => {
    setDragOverGroup(null);
    setDragPosition(null);
  };

  const handleGroupDrop = async (e: React.DragEvent, targetGroupId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedGroup || draggedGroup === targetGroupId) {
      setDraggedGroup(null);
      setDragOverGroup(null);
      setDragPosition(null);
      return;
    }

    const groups = arrayOrEmpty(selectedAutomation?.groups);
    const draggedIdx = groups.findIndex((g) => g.id === draggedGroup);
    const targetIdx = groups.findIndex((g) => g.id === targetGroupId);
    if (draggedIdx === -1 || targetIdx === -1) return;

    const newGroups = [...groups];
    const [removed] = newGroups.splice(draggedIdx, 1);
    let insertIdx = targetIdx;
    if (dragPosition === "after") insertIdx = targetIdx + 1;
    newGroups.splice(insertIdx, 0, removed);

    try {
      await updateAutomation({
        id: selectedAutomation._id,
        groups: newGroups
      });
      //onGroupsChange(newGroups);

      setDraggedGroup(null);
      setDragOverGroup(null);
      setDragPosition(null);
    } catch (err) {
      toast.error("Error updating");
    }
  };

  const handleAddCommand = async (templates: AutomationGroup) => {
    try {
      const group = {
        ...templates,
        id: uid(),
        commands: templates.commands.map((cmd) => ({ ...cmd, id: uid() }))
      };
      await addAutomationGroup({ id: selectedAutomation._id, group });
      selectedAutomation.groups.push(group);
      setOpenAddCommandModal(false);
    } catch (err) {
      toast.error("Unable to add automation");
    }
  };
  // Handle group drag end
  const handleGroupDragEnd = () => {
    setDraggedGroup(null);
    setDragOverGroup(null);
    setDragPosition(null);
  };

  return (
    <CommandsContainer ref={containerRef}>
      <CommandsListSection>
        <HeaderBar>
          <Typography.Text strong>Commands</Typography.Text>
          <IconButton
            disabled={!selectedAutomation}
            onClick={() => setOpenAddCommandModal(true)}
            icon={<Icon path={mdiPlus} size={1.2} />}
            size="small"
          />
        </HeaderBar>
        <DragHint>
          <span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Drag commands to reorder
            </Typography.Text>
          </span>
          <Tooltip title="Collapse All">
            <CollapseAllButton
              type="text"
              icon={<Icon path={mdiArrowCollapseAll} size={1} />}
              onClick={() => {
                if (selectedAutomation?.groups) {
                  const allCollapsed: Record<string, boolean> = {};
                  selectedAutomation.groups.forEach((group: { id: string }) => {
                    allCollapsed[group.id] = true;
                  });
                  setCollapsedGroups(allCollapsed);
                }
              }}
              size="small"
            />
          </Tooltip>
        </DragHint>
        <GroupsContainer>
          {arrayOrEmpty(selectedAutomation?.groups).map((group) => {
            const isCollapsed = collapsedGroups[group.id];
            const isDragging = draggedGroup === group.id;
            const isDragOver = dragOverGroup === group.id && draggedGroup !== null;
            return (
              <GroupWrapper
                key={group.id}
                draggable
                onDragStart={(e) => handleGroupDragStart(e, group.id)}
                onDragOver={(e) => handleGroupDragOver(e, group.id)}
                onDragLeave={handleGroupDragLeave}
                onDrop={(e) => handleGroupDrop(e, group.id)}
                onDragEnd={handleGroupDragEnd}
                style={{
                  opacity: isDragging ? 0.5 : 1,
                  background: isDragOver ? "#e6f7ff" : isDragging ? "#f0f0f0" : "#fafbfc",
                  border:
                    isDragOver || isDragging ? "2px solid #1890ff" : "1px solid lightgray"
                }}>
                <GroupHeader>
                  <GroupTitle>
                    <IconButton
                      type="text"
                      icon={
                        <Icon
                          path={isCollapsed ? mdiChevronRight : mdiChevronDown}
                          size={1}
                        />
                      }
                      size="small"
                      onClick={() => toggleGroupCollapse(group.id)}
                    />
                    <span>{group.name || "Group"}</span>
                  </GroupTitle>
                  <Popover
                    overlayClassName="popover-no-padding"
                    content={
                      <div>
                        <Button
                          type="text"
                          onClick={() => duplicateGroup(group)}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: 10
                          }}>
                          <Icon
                            path={mdiContentCopy}
                            size={0.9}
                            style={{ marginRight: 6 }}
                          />
                          Duplicate
                        </Button>
                        <Button
                          type="text"
                          danger
                          onClick={() => handleDeleteGroup(group.id)}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: 10
                          }}>
                          <Icon path={mdiDelete} size={0.9} style={{ marginRight: 6 }} />
                          Delete
                        </Button>
                      </div>
                    }
                    trigger="click"
                    open={popoverOpen === group.id}
                    onOpenChange={(open) => setPopoverOpen(open ? group.id : null)}>
                    <IconButton
                      type="text"
                      icon={<Icon path={mdiDotsVertical} size={1} />}
                      size="small"
                    />
                  </Popover>
                </GroupHeader>
                {!isCollapsed &&
                  arrayOrEmpty(group.commands).map((command) => (
                    <CommandItem
                      key={command.id}
                      onClick={() => onSelectedCommand(command)}
                      selected={selectedCommand == command}
                      isDragging={false}
                      isDragOver={false}>
                      {getRenderComponent(command)}
                    </CommandItem>
                  ))}
              </GroupWrapper>
            );
          })}
        </GroupsContainer>
      </CommandsListSection>

      <PropertiesContent>
        <PropertiesHeader>
          <Typography.Text strong>Properties</Typography.Text>
        </PropertiesHeader>
        <PropertiesScrollable>
          <PropertiesInner>
            {getSettingsComponent(selectedCommand, handleSettingsChange)}
          </PropertiesInner>
        </PropertiesScrollable>
      </PropertiesContent>
      {/* Rename Modal */}
      <Modal
        getContainer={() => containerRef.current}
        title="Rename Group"
        open={renameModalOpen}
        onOk={() => {
          if (
            renameTargetGroup &&
            renameValue.trim() &&
            renameValue !== renameTargetGroup.name
          ) {
            const newGroups = arrayOrEmpty(selectedAutomation?.groups).map((g) =>
              g.id === renameTargetGroup.id ? { ...g, name: renameValue.trim() } : g
            );
            onGroupsChange(newGroups);
          }
          setRenameModalOpen(false);
          setRenameTargetGroup(null);
        }}
        onCancel={() => setRenameModalOpen(false)}
        okText="Rename">
        <Input
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onPressEnter={() => {
            if (
              renameTargetGroup &&
              renameValue.trim() &&
              renameValue !== renameTargetGroup.name
            ) {
              const newGroups = arrayOrEmpty(selectedAutomation?.groups).map((g) =>
                g.id === renameTargetGroup.id ? { ...g, name: renameValue.trim() } : g
              );
              onGroupsChange(newGroups);
            }
            setRenameModalOpen(false);
            setRenameTargetGroup(null);
          }}
          autoFocus
          placeholder="Enter new group name"
        />
      </Modal>

      <AddCommandModal
        open={openAddCommandModal}
        commands={commandsTemplate}
        onCancel={() => setOpenAddCommandModal(false)}
        onAddCommand={handleAddCommand}
      />
    </CommandsContainer>
  );
};

export default AutomationCommands;
